/**
 * AuditTrail Component
 * Displays chronological action list for a finding (newest first)
 * Used in FindingCard expanded view per WF-5/WF-8 wireframes
 */

import { useAuditTrail, formatAuditAction, getAuditActionIcon } from '../hooks/useAuditTrail';
import { formatDateLong } from '../utils/formatDate';

export interface AuditTrailProps {
  resourceType: string;
  resourceId: string;
}

const iconPaths: Record<string, string> = {
  clipboard: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  camera: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z',
  check: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  x: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
};

const iconColors: Record<string, string> = {
  clipboard: 'text-gray-400 dark:text-slate-500',
  shield: 'text-gray-500 dark:text-slate-400',
  camera: 'text-gray-500 dark:text-slate-400',
  check: 'text-gray-500 dark:text-slate-400',
  x: 'text-gray-500 dark:text-slate-400',
  clock: 'text-gray-400 dark:text-slate-500',
};

export function AuditTrail({ resourceType, resourceId }: AuditTrailProps) {
  const { data: entries, isLoading } = useAuditTrail(resourceType, resourceId);

  if (isLoading) {
    return (
      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800">
        <h4 className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">
          Audit History
        </h4>
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-start gap-2 animate-pulse">
              <div className="w-4 h-4 bg-gray-200 dark:bg-slate-700 rounded-full mt-0.5" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
                <div className="h-2 bg-gray-100 dark:bg-slate-800 rounded w-1/2 mt-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return null;
  }

  return (
    <div data-testid="audit-trail" className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800">
      <h4 className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">
        Audit History
      </h4>
      <div data-testid="audit-trail-entries" className="space-y-2">
        {entries.map((entry) => {
          const iconName = getAuditActionIcon(entry.action);
          const path = iconPaths[iconName];
          const color = iconColors[iconName];

          return (
            <div key={entry.id} data-testid={`audit-entry-${entry.id}`} className="flex items-start gap-2">
              <svg
                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={path}
                />
              </svg>
              <div className="flex-1 min-w-0">
                <p data-testid="audit-entry-action" className="text-xs text-gray-700 dark:text-slate-300">
                  {formatAuditAction(entry)}
                  {entry.details && 'photo_count' in entry.details
                    ? ` (${entry.details.photo_count} photos)`
                    : ''}
                </p>
                <p data-testid="audit-entry-timestamp" className="text-xs text-gray-400 dark:text-slate-500">
                  {formatDateLong(entry.created_at)}
                  {entry.actor_name ? <span data-testid="audit-entry-user"> by {entry.actor_name}</span> : ''}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AuditTrail;
